// Cozy header: rotating announcement bar, shrink-on-scroll header, desktop dropdowns,
// mobile menu drawer and search sheet. Drawers are native <dialog> (focus trap, Esc, inert page).

// Reduced motion = the OS setting or "stop animations" in the accessibility menu (cozy-a11y.js).
const cozyReducedMotion = () =>
  window.cozyCalm ? window.cozyCalm() : matchMedia('(prefers-reduced-motion: reduce)').matches;

// Shared: close any cozy <dialog> on backdrop click or [data-drawer-close].
if (!window.cozyDialogs) {
  window.cozyDialogs = true;
  document.addEventListener('click', (e) => {
    const closer = e.target.closest('[data-drawer-close]');
    const dialog = closer ? closer.closest('dialog') : e.target instanceof HTMLDialogElement ? e.target : null;
    if (dialog?.open) dialog.close();
  });
}

if (!customElements.get('cozy-announcement')) {
  customElements.define(
    'cozy-announcement',
    class CozyAnnouncement extends HTMLElement {
      connectedCallback() {
        this.slides = [...this.querySelectorAll('[data-announce-slide]')];
        if (this.slides.length < 2) return;
        this.index = 0;
        this.interval = Number(this.dataset.interval) || 5000;
        this.stopped = cozyReducedMotion();

        this.querySelector('[data-announce-prev]').addEventListener('click', () => this.manual(-1));
        this.querySelector('[data-announce-next]').addEventListener('click', () => this.manual(1));
        // Pause while the visitor is reading or tabbing through.
        this.addEventListener('mouseenter', () => this.halt());
        this.addEventListener('mouseleave', () => this.start());
        this.addEventListener('focusin', () => this.halt());
        this.addEventListener('focusout', () => this.start());
        // Accessibility menu: stop rotating; turning it off resumes unless the arrows took over.
        this.onStill = (e) => this.setStopped(e.detail.still || this.manualStop || cozyReducedMotion());
        document.addEventListener('cozy:still', this.onStill);

        this.setStopped(this.stopped);
      }

      disconnectedCallback() {
        this.halt();
        if (this.onStill) document.removeEventListener('cozy:still', this.onStill);
      }

      show(next) {
        const count = this.slides.length;
        this.index = (next + count) % count;
        this.slides.forEach((slide, i) => {
          const active = i === this.index;
          slide.classList.toggle('is-active', active);
          slide.inert = !active;
        });
      }

      manual(step) {
        this.show(this.index + step);
        this.manualStop = true;
        this.setStopped(true); // explicit navigation = visitor takes control
      }

      setStopped(stopped) {
        this.stopped = stopped;
        this.classList.toggle('is-paused', stopped);
        stopped ? this.halt() : this.start();
      }

      start() {
        if (this.stopped || this.timer) return;
        this.timer = setInterval(() => this.show(this.index + 1), this.interval);
      }

      halt() {
        clearInterval(this.timer);
        this.timer = null;
      }
    }
  );
}

if (!customElements.get('cozy-header')) {
  customElements.define(
    'cozy-header',
    class CozyHeader extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('.cozy-header-section') || this;
        this.menu = this.querySelector('[data-menu-drawer]');
        this.search = this.querySelector('[data-search-dialog]');
        this.burger = this.querySelector('[data-menu-open]');

        // "Scrolled" state without a scroll listener: a 1px sentinel sits just below the point where
        // the header sticks (announcement height + 4px); once it leaves the viewport, we're scrolled.
        this.sentinel = document.createElement('div');
        this.sentinel.setAttribute('aria-hidden', 'true');
        // display:block is required: Dawn hides div:empty, and a hidden target never "intersects".
        this.sentinel.style.cssText = `display:block;position:absolute;inset-inline:0;top:${this.section.offsetTop + 4}px;height:1px;pointer-events:none;`;
        document.body.prepend(this.sentinel);
        this.scrollObserver = new IntersectionObserver(([entry]) =>
          this.section.classList.toggle('is-scrolled', !entry.isIntersecting)
        );
        this.scrollObserver.observe(this.sentinel);

        // Dawn sections (and our sticky gallery) offset themselves by --header-height.
        const bar = this.querySelector('.cozy-header__bar');
        this.resizeObserver = new ResizeObserver(() =>
          document.documentElement.style.setProperty('--header-height', `${bar.offsetHeight}px`)
        );
        this.resizeObserver.observe(bar);

        this.burger?.addEventListener('click', () => {
          this.menu.showModal();
          this.burger.setAttribute('aria-expanded', 'true');
        });
        this.menu?.addEventListener('close', () => this.burger?.setAttribute('aria-expanded', 'false'));

        this.querySelectorAll('[data-search-open]').forEach((btn) =>
          btn.addEventListener('click', () => {
            this.search.showModal();
            this.search.querySelector('input[type="search"]').focus();
          })
        );

        this.initDropdowns();
      }

      disconnectedCallback() {
        this.scrollObserver?.disconnect();
        this.sentinel?.remove();
        document.removeEventListener('click', this.onOutsideClick);
        this.resizeObserver?.disconnect();
      }

      initDropdowns() {
        const dropdowns = [...this.querySelectorAll('[data-dropdown]')];
        if (!dropdowns.length) return;
        const hoverable = matchMedia('(hover: hover)');

        dropdowns.forEach((details) => {
          let closeTimer;
          details.addEventListener('mouseenter', () => {
            if (!hoverable.matches) return;
            clearTimeout(closeTimer);
            dropdowns.forEach((d) => d !== details && d.removeAttribute('open'));
            details.setAttribute('open', '');
          });
          details.addEventListener('mouseleave', () => {
            if (!hoverable.matches) return;
            closeTimer = setTimeout(() => details.removeAttribute('open'), 150);
          });
          details.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape' || !details.open) return;
            details.removeAttribute('open');
            details.querySelector('summary').focus();
          });
          details.addEventListener('toggle', () => {
            if (details.open) dropdowns.forEach((d) => d !== details && d.removeAttribute('open'));
          });
        });

        this.onOutsideClick = (e) => {
          dropdowns.forEach((d) => d.open && !d.contains(e.target) && d.removeAttribute('open'));
        };
        document.addEventListener('click', this.onOutsideClick);
      }
    }
  );
}
